import React, { useState, useEffect } from "react";
import { useStores } from "../stores";
import { apiPostPut, apiGet } from "../api/api_methods";
import {
  rolesWithId,
  PLAN_DURATIONS,
  PLAN_TYPE_DURATIONS,
} from "../constants/constants";
import Loader from "./reusableComponents/loader";
import ReactPaginate from "react-paginate";
import {
  IoRefresh,
  IoCalendar,
  IoMail,
  IoPerson,
  IoDocumentText,
} from "react-icons/io5";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid Date";
  }
};

// Helper function to calculate renewal date based on plan duration
const calculateRenewalDate = (createdAt, planDuration) => {
  if (!createdAt || !planDuration) return null;

  try {
    const createdDate = new Date(createdAt);
    
    // Check if date is valid
    if (isNaN(createdDate.getTime())) {
      console.warn("Invalid createdAt date:", createdAt);
      return null;
    }

    const renewalDate = new Date(createdDate);
    renewalDate.setMonth(renewalDate.getMonth() + planDuration);

    // Verify the renewal date is valid
    if (isNaN(renewalDate.getTime())) {
      console.warn("Invalid renewal date calculated:", { createdAt, planDuration });
      return null;
    }

    return renewalDate;
  } catch (error) {
    console.error("Error calculating renewal date:", error, { createdAt, planDuration });
    return null;
  }
};

// Helper function to check if form needs renewal
const needsRenewal = (createdAt, planDuration, renewalSent = false) => {
  // If renewal has been sent, still calculate status but mark as renewed
  // This allows the form to show in the list with "Renewal Sent" badge
  if (!createdAt || !planDuration) {
    // If missing required data, return null to indicate invalid
    return null;
  }

  const renewalDate = calculateRenewalDate(createdAt, planDuration);
  if (!renewalDate) {
    return null;
  }

  const now = new Date();
  const daysUntilRenewal = Math.ceil(
    (renewalDate - now) / (1000 * 60 * 60 * 24)
  );

  // Return renewal status: overdue, due-soon, or active
  // Note: renewalSent flag is handled separately in the UI
  if (daysUntilRenewal < 0) return "overdue";
  if (daysUntilRenewal <= 30) return "due-soon";
  return "active";
};

// Helper function to get plan duration in months
const getPlanDurationInMonths = (planType, customDuration = null) => {
  if (customDuration) {
    return customDuration;
  }
  return PLAN_TYPE_DURATIONS[planType] || PLAN_DURATIONS.ANNUALLY;
};

// Helper function to get renewal type label
const getRenewalTypeLabel = (duration) => {
  switch (duration) {
    case PLAN_DURATIONS.QUARTERLY:
      return "Quarterly";
    case PLAN_DURATIONS.HALF_YEARLY:
      return "Half Yearly";
    case PLAN_DURATIONS.ANNUALLY:
      return "Annually";
    case PLAN_DURATIONS.BIENNIALLY:
      return "Biennially";
    default:
      return "Custom";
  }
};

// Helper to convert duration string to months
const convertDurationStringToMonths = (durationStr) => {
  if (!durationStr && durationStr !== 0) return 12; // Default to annually

  // If it's already a number, treat it as months
  if (typeof durationStr === "number") {
    return durationStr || 12;
  }

  // Normalize the string: trim whitespace, convert to lowercase, handle variations
  const normalized = String(durationStr).trim().toLowerCase().replace(/\s+/g, " ");

  if (normalized === "quarterly") return 3;
  if (normalized === "half yearly" || normalized === "half-yearly" || normalized === "halfyearly") return 6;
  if (normalized === "annually" || normalized === "annual") return 12;
  if (normalized === "biennially" || normalized === "biennial") return 24;

  // Try to parse number if string format like "12 months"
  const num = parseInt(durationStr);
  if (!isNaN(num)) return num;

  // Default to annually if we can't parse
  console.warn("Unknown duration format, defaulting to 12 months:", durationStr);
  return 12;
};

// Workflow status mapping based on numeric codes
const WORKFLOW_STATUS_MAP = {
  1: "Assigned",
  3: "Submitted for Approval",
  4: "Admin Approved",
  7: "Shared with Client",
  8: "Locked",
};

function Renewal() {
  const { clientsStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [renewalForms, setRenewalForms] = useState([]);
  const [filter, setFilter] = useState("all"); // all, overdue, due-soon, active, renewed
  const [message] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedPlanType, setSelectedPlanType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  // Wrap fetchRenewalForms in useCallback to avoid changing on every render
  const fetchRenewalForms = React.useCallback(async () => {
    try {
      setLoading(true);

      // Debug: Log current user info
      console.log("Current user:", clientsStore.user);

      let forms = [];
      const allPlanTypes = [
        "basic",
        "comprehensive",
        "zeroToOne",
        "wealthManagement",
      ];

      // Helper to map client data
      const mapClientData = (client, defaultPlanType) => {
        const planDetails = client.awareness?.planDetails || {};
        const personalDetails = client.awareness?.personalDetails || {};

        // Map fields to expected format
        const planType = planDetails.planType || client.planType || defaultPlanType || "basic";

        // handle duration: "Quarterly" string or numeric
        let durationStr = planDetails.planDuration || client.planDuration || planDetails.duration || "Annually";
        const duration = convertDurationStringToMonths(durationStr);

        return {
          ...client,
          planType: planType,
          clientName: personalDetails.name || client.name || "N/A",
          email: personalDetails.email || client.email,
          mobileNumber: personalDetails.phone || personalDetails.mobileNumber || client.phone || client.mobile,
          createdAt: client.createdAt || client.createdOn,
          planDuration: duration,
          workflowStatus: client.status, // Keep the numeric status
          renewalSent: client.renewalSent || client.renewalEmailSent || false // Extract renewalSent from backend
        };
      };

      // Check if store data is available
      const hasStoreData = allPlanTypes.some(
        (planType) =>
          clientsStore[planType]?.all && clientsStore[planType].all.length > 0
      );

      if (hasStoreData) {
        console.log("Using existing store data for renewal forms");

        // Collect all forms from the store
        allPlanTypes.forEach((planType) => {
          if (
            clientsStore[planType]?.all &&
            Array.isArray(clientsStore[planType].all)
          ) {
            const planForms = clientsStore[planType].all.map((client) =>
              mapClientData(client, planType)
            );
            forms.push(...planForms);
          }
        });

        console.log("Forms from store:", forms.length);
      } else {
        console.log("No store data available, fetching from API");

        // Fetch all clients using the get-clients endpoint with high limit
        for (const planType of allPlanTypes) {
          try {
            const user = clientsStore.user;
            let apiPath;
            
            // Use GET endpoint with limit=1000 to fetch all clients
            if (user.role === rolesWithId.agent) {
              apiPath = `/adminPanel/get-clients?planType=${planType}&status=&agentId=${user._id}&page=1&limit=1000`;
            } else {
              apiPath = `/adminPanel/get-clients?planType=${planType}&status=&page=1&limit=1000`;
            }

            console.log(`Fetching ${planType} forms from:`, apiPath);

            const response = await apiGet(apiPath);

            if (response.status === 200 && response.body.success) {
              // Handle different possible response structures
              const planForms = response.body.data?.clients || 
                               response.body.data?.all || 
                               response.body.data || 
                               [];
              console.log(`${planType} forms fetched:`, planForms.length);

              // Process and map the forms
              const processedForms = planForms.map(client =>
                mapClientData(client, planType)
              );

              forms.push(...processedForms);
            } else if (response.status === 200 && response.body.data) {
              // Fallback: try the old response structure
              const planForms = response.body.data.all || response.body.data || [];
              console.log(`${planType} forms fetched (fallback):`, planForms.length);

              const processedForms = planForms.map(client =>
                mapClientData(client, planType)
              );

              forms.push(...processedForms);
            }
          } catch (error) {
            console.log(`Error fetching ${planType} forms:`, error);
          }
        }
      }

      console.log("Total forms processed:", forms.length);

      // Add calculated renewal dates and status
      forms = forms.map((form, index) => {
        const renewalDate = calculateRenewalDate(form.createdAt, form.planDuration);
        const renewalStatus = needsRenewal(
          form.createdAt,
          form.planDuration,
          form.renewalSent
        );

        // Debug logging for all forms to see date calculations
        if (form.createdAt) {
          const createdDate = new Date(form.createdAt);
          const year = createdDate.getFullYear();
          const now = new Date();
          const daysUntilRenewal = renewalDate 
            ? Math.ceil((renewalDate - now) / (1000 * 60 * 60 * 24))
            : null;
          
          // Log forms from 2024 or earlier
          if (year < 2025) {
            console.log("Form from 2024 or earlier:", {
              id: form._id,
              name: form.clientName,
              createdAt: form.createdAt,
              createdYear: year,
              planDuration: form.planDuration,
              renewalDate: renewalDate ? renewalDate.toISOString() : null,
              daysUntilRenewal: daysUntilRenewal,
              renewalStatus: renewalStatus,
              renewalSent: form.renewalSent,
            });
          }

          // Log first 3 forms for debugging
          if (index < 3) {
            console.log(`Sample form ${index + 1}:`, {
              name: form.clientName,
              createdAt: form.createdAt,
              createdYear: year,
              planDuration: form.planDuration,
              renewalDate: renewalDate ? renewalDate.toISOString() : null,
              daysUntilRenewal: daysUntilRenewal,
              renewalStatus: renewalStatus,
              now: now.toISOString(),
            });
          }
        }

        return {
          ...form,
          calculatedRenewalDate: renewalDate,
          renewalStatus: renewalStatus || "active", // Default to active if null
        };
      });

      // Filter out forms with invalid dates or durations (optional - comment out if you want to see all forms)
      // forms = forms.filter(form => form.calculatedRenewalDate !== null);

      console.log("Final forms to display:", forms.length);
      console.log("Forms by status:", {
        overdue: forms.filter(f => f.renewalStatus === "overdue").length,
        dueSoon: forms.filter(f => f.renewalStatus === "due-soon").length,
        active: forms.filter(f => f.renewalStatus === "active").length,
        renewed: forms.filter(f => f.renewalSent).length,
      });
      setRenewalForms(forms);
    } catch (error) {
      console.error("Error fetching renewal forms:", error);
      setRenewalForms([]);
    } finally {
      setLoading(false);
    }
  }, [clientsStore]);

  useEffect(() => {
    // Wait for user data to be loaded
    if (clientsStore.user && clientsStore.user._id) {
      fetchRenewalForms();
    }
  }, [clientsStore.user, fetchRenewalForms]);

  // Also fetch when component mounts if user data is already available
  useEffect(() => {
    if (
      clientsStore.user &&
      clientsStore.user._id &&
      renewalForms.length === 0
    ) {
      fetchRenewalForms();
    }
  }, [clientsStore.user, fetchRenewalForms, renewalForms.length]);

  // Filter forms based on current user role
  const getFilteredForms = () => {
    let forms = renewalForms;

    // Filter by plan type
    if (selectedPlanType !== "all") {
      forms = forms.filter((form) => form.planType === selectedPlanType);
    }

    // Filter by search term
    if (searchTerm) {
      forms = forms.filter((form) => {
        const clientName = String(
          form.clientName ||
          form.name ||
          form.awareness?.personalDetails?.name ||
          ""
        );
        const planType = String(form.planType || "");
        const phoneNumber = String(
          form.mobileNumber ||
          form.mobile ||
          form.awareness?.personalDetails?.mobileNumber ||
          form.awareness?.personalDetails?.mobile ||
          form.awareness?.personalDetails?.phone ||
          ""
        );
        const searchLower = searchTerm.toLowerCase();
        return (
          clientName.toLowerCase().includes(searchLower) ||
          planType.toLowerCase().includes(searchLower) ||
          phoneNumber.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter by renewal status
    if (filter !== "all") {
      forms = forms.filter((form) => {
        // Special case: show forms where renewal email has been sent
        if (filter === "renewed") {
          return !!form.renewalSent;
        }

        const duration = getPlanDurationInMonths(
          form.planType,
          form.customDuration
        );
        const renewalStatus = needsRenewal(
          form.createdAt,
          duration,
          form.renewalSent
        );
        return renewalStatus === filter;
      });
    }

    // Sort forms: overdue first, then due-soon, then active, then renewed
    // Within each category, sort by renewal date (most urgent first)
    forms.sort((a, b) => {
      // Priority order: overdue > due-soon > active > renewed
      const statusPriority = {
        overdue: 1,
        "due-soon": 2,
        active: 3,
        renewed: 4,
      };

      // Get status for comparison (use renewalSent for renewed status)
      const aStatus = a.renewalSent ? "renewed" : (a.renewalStatus || "active");
      const bStatus = b.renewalSent ? "renewed" : (b.renewalStatus || "active");

      const aPriority = statusPriority[aStatus] || 5;
      const bPriority = statusPriority[bStatus] || 5;

      // If different priorities, sort by priority
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // If same priority, sort by renewal date (earliest first)
      // For overdue/due-soon, most overdue should be first
      // For active, soonest renewal should be first
      const aDate = a.calculatedRenewalDate ? new Date(a.calculatedRenewalDate).getTime() : Infinity;
      const bDate = b.calculatedRenewalDate ? new Date(b.calculatedRenewalDate).getTime() : Infinity;

      if (aPriority === 1 || aPriority === 2) {
        // For overdue and due-soon, most overdue (earliest date) first
        return aDate - bDate;
      } else {
        // For active and renewed, soonest renewal first
        return aDate - bDate;
      }
    });

    return forms;
  };

  const toggleRowExpansion = (formId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(formId)) {
      newExpandedRows.delete(formId);
    } else {
      newExpandedRows.add(formId);
    }
    setExpandedRows(newExpandedRows);
  };

  // ...existing code...

  const handleSendRenewal = async (form) => {
    setSelectedForm(form);

    // Get client email
    const clientEmail =
      form.email || form.awareness?.personalDetails?.email || "";

    // Safe display helper
    const displayOrDash = (value) => {
      if (value === 0) return "0";
      return value || "-";
    };

    const clientName =
      form.clientName ||
      form.name ||
      form.awareness?.personalDetails?.name ||
      "Valued Client";

    const planType = form.planType ? clientsStore.camelToTitleCase(form.planType) : "-";
    const durationLabel = getRenewalTypeLabel(
      convertDurationStringToMonths(form.planDuration || form.customDuration)
    );
    const renewalDateLabel = formatDate(form.calculatedRenewalDate);

    // Set up styled HTML email template
    const emailTemplate = {
      to: clientEmail,
      subject: `Renewal Reminder - ${planType} Plan`,
      body: `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; background-color: #f5f7fb; padding: 24px;">
          <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 640px; margin: 0 auto; background: #ffffff; border-radius: 12px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06); overflow: hidden;">
            <tr>
              <td style="padding: 20px 24px 14px; text-align: center; border-bottom: 1px solid #e5e7eb;">
                <div style="display: inline-block; padding: 10px 16px; background: #111827; color: #ffffff; border-radius: 999px; font-weight: 600; letter-spacing: 0.2px;">Awaken - Financial Journey</div>
                <h2 style="margin: 14px 0 6px; color: #111827; font-size: 20px;">Plan renewal reminder</h2>
                <p style="margin: 0; color: #4b5563; font-size: 14px;">Your financial planning period is coming up for renewal.</p>
              </td>
            </tr>
            <tr>
              <td style="padding: 18px 24px 8px;">
                <p style="margin: 0 0 16px; color: #111827; font-size: 14px;">
                  Dear <strong>${clientName}</strong>,
                </p>
                <p style="margin: 0 0 16px; color: #4b5563; font-size: 14px; line-height: 1.6;">
                  We hope your financial planning journey with us has been valuable. The details of your current plan are given below. Please review them and get in touch with us to renew your plan so that your advice and support continues without interruption.
                </p>

                <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse; margin-bottom: 16px;">
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.35px; width: 38%;">Client Name</td>
                    <td style="padding: 8px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(clientName)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.35px;">Plan Type</td>
                    <td style="padding: 8px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(planType)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.35px;">Current Duration</td>
                    <td style="padding: 8px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(durationLabel)}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; color: #6b7280; font-size: 13px; text-transform: uppercase; letter-spacing: 0.35px;">Renewal Date</td>
                    <td style="padding: 8px 0; color: #111827; font-size: 14px; font-weight: 600;">${displayOrDash(renewalDateLabel)}</td>
                  </tr>
                </table>

                <p style="margin: 0 0 12px; color: #4b5563; font-size: 14px; line-height: 1.6;">
                  To renew your plan or discuss changes to your goals, please reply to this email or contact your financial planner.
                </p>
                <p style="margin: 0; color: #4b5563; font-size: 14px; line-height: 1.6;">
                  We look forward to supporting the next phase of your financial journey.
                </p>
              </td>
            </tr>
            <tr>
              <td style="padding: 0 24px 20px; text-align: center; color: #6b7280; font-size: 13px;">
                <p style="margin: 0 0 6px;">Regards,</p>
                <p style="margin: 0; color: #111827; font-weight: 600;">Awaken - Financial Journey</p>
              </td>
            </tr>
          </table>
        </div>
      `,
    };

    setEmailData(emailTemplate);
    setShowEmailModal(true);
  };

  const sendRenewalEmail = async () => {
    if (!emailData.to || !emailData.subject || !emailData.body) {
      alert("Please fill To, Subject and Message before sending.");
      return;
    }

    try {
      setSendingEmail(true);
      const senderName =
        clientsStore.user?.displayName ||
        clientsStore.user?.name ||
        clientsStore.user?.email ||
        "Awaken Team";

      const payload = {
        to: [emailData.to],
        subject: emailData.subject,
        html: emailData.body,
        from: `${senderName} <awaken@mainstream.co.in>`,
      };

      const response = await apiPostPut(
        payload,
        "/adminPanel/send-email",
        "POST"
      );

      if (response.status === 200) {
        // Update backend to persist renewalSent status
        if (selectedForm?._id) {
          try {
            // Try the dedicated renewal status endpoint first
            let updateResponse = await apiPostPut(
              {
                formId: selectedForm._id,
                renewalSent: true,
                renewalSentDate: new Date().toISOString(),
              },
              "/adminPanel/update-renewal-status",
              "PUT"
            );

            // If that endpoint doesn't exist (404), try alternative endpoint
            if (updateResponse.status === 404) {
              console.log("Dedicated renewal endpoint not found, trying alternative...");
              // Alternative: Update via general client/form update endpoint
              updateResponse = await apiPostPut(
                {
                  renewalSent: true,
                  renewalSentDate: new Date().toISOString(),
                },
                `/adminPanel/clients/${selectedForm._id}`,
                "PUT"
              );
            }

            if (updateResponse.status === 200) {
              console.log("Renewal status updated in backend successfully");
            } else {
              console.warn("Failed to update renewal status in backend:", updateResponse.body?.message);
              console.warn("Status will be lost on page refresh. Backend endpoint needs to be implemented.");
              // Continue anyway - email was sent successfully
            }
          } catch (updateError) {
            console.error("Error updating renewal status:", updateError);
            console.warn("Status will be lost on page refresh. Backend endpoint needs to be implemented.");
            // Continue anyway - email was sent successfully
          }
        }

        alert("Renewal email sent successfully.");
        
        // Update local state
        if (selectedForm?._id) {
          setRenewalForms((prev) =>
            prev.map((form) => {
              if (form._id !== selectedForm._id) return form;
              const updated = {
                ...form,
                renewalSent: true,
                renewalSentDate: new Date().toISOString(),
              };
              // Recalculate renewal status with the new flag
              const newStatus = needsRenewal(
                updated.createdAt,
                updated.planDuration,
                updated.renewalSent
              );
              return {
                ...updated,
                renewalStatus: newStatus,
              };
            })
          );
        }
        setShowEmailModal(false);
      } else {
        alert(response.body?.message || "Failed to send renewal email.");
      }
    } catch (error) {
      console.error("Error sending renewal email:", error);
      alert("Error sending renewal email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadge = (form) => {
    // Show workflow status if available, combined with renewal status
    const workflowLabel = WORKFLOW_STATUS_MAP[form.workflowStatus] || "Unknown";

    // Renewal Badge
    let renewalBadge = null;
    if (form.renewalSent) {
      renewalBadge = (
        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
          Renewal Sent
        </span>
      );
    } else if (form.renewalStatus === "overdue") {
      renewalBadge = (
        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
          Overdue
        </span>
      );
    } else if (form.renewalStatus === "due-soon") {
      renewalBadge = (
        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
          Due Soon
        </span>
      );
    } else {
      renewalBadge = (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
          Active
        </span>
      );
    }

    return (
      <div className="flex flex-col gap-1 items-start">
        {renewalBadge}
        {form.workflowStatus && (
          <span className="text-[10px] text-gray-500 border border-gray-200 px-1 rounded">
            {workflowLabel}
          </span>
        )}
      </div>
    );
  };

  const getDaysUntilRenewal = (form) => {
    if (!form.calculatedRenewalDate) return "N/A";

    const now = new Date();
    const daysUntilRenewal = Math.ceil(
      (form.calculatedRenewalDate - now) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilRenewal < 0) {
      return `${Math.abs(daysUntilRenewal)} days overdue`;
    } else if (daysUntilRenewal === 0) {
      return "Due today";
    } else if (daysUntilRenewal === 1) {
      return "Due tomorrow";
    } else {
      return `Due in ${daysUntilRenewal} days`;
    }
  };

  const filteredForms = getFilteredForms();

  // Reset to first page when filters or search change
  useEffect(() => {
    setCurrentPage(0);
  }, [filter, selectedPlanType, searchTerm]);

  // Calculate pagination
  const pageCount = Math.ceil(filteredForms.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedForms = filteredForms.slice(startIndex, endIndex);

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
    // Close expanded rows when page changes
    setExpandedRows(new Set());
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <IoRefresh className="text-blue-600" />
          Renewal Management
        </h2>
        <div className="text-sm text-gray-600">
          {clientsStore.user.role === rolesWithId.agent
            ? "Financial Planner View"
            : "Admin View"}
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 p-3 rounded ${message.includes("successfully")
            ? "bg-green-100 text-green-700 border border-green-300"
            : "bg-red-100 text-red-700 border border-red-300"
            }`}
        >
          {message}
        </div>
      )}

      {/* Filter Controls */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Status:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Forms</option>
              <option value="overdue">Overdue</option>
              <option value="due-soon">Due Soon (≤30 days)</option>
              <option value="active">Active</option>
              <option value="renewed">Renewal Sent</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">
              Plan Type:
            </label>
            <select
              value={selectedPlanType}
              onChange={(e) => setSelectedPlanType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Plans</option>
              <option value="basic">Basic</option>
              <option value="comprehensive">Comprehensive</option>
              <option value="zeroToOne">Zero2One</option>
              <option value="wealthManagement">Wealth</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Search:</label>
            <input
              type="text"
              placeholder="Search by client name or plan type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>

          <button
            onClick={fetchRenewalForms}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <IoRefresh className="text-sm" />
            Refresh
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-blue-800 font-semibold text-lg">
              {
                filteredForms.filter((f) => f.renewalStatus === "overdue")
                  .length
              }
            </div>
            <div className="text-blue-600 text-sm">Overdue</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-yellow-800 font-semibold text-lg">
              {
                filteredForms.filter((f) => f.renewalStatus === "due-soon")
                  .length
              }
            </div>
            <div className="text-yellow-600 text-sm">Due Soon</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-green-800 font-semibold text-lg">
              {filteredForms.filter((f) => f.renewalSent).length}
            </div>
            <div className="text-green-600 text-sm">Renewal Sent</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-gray-800 font-semibold text-lg">
              {filteredForms.length}
            </div>
            <div className="text-gray-600 text-sm">Total Forms</div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Forms Table */}
      {!loading && (
        <div className="overflow-x-auto max-w-full">
          {filteredForms.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <IoDocumentText className="text-4xl mx-auto mb-2 text-gray-300" />
              {renewalForms.length === 0 ? (
                <div>
                  <p className="text-lg font-medium mb-2">No forms found</p>
                  <p className="text-sm text-gray-400 mb-4">
                    {clientsStore.user.role === rolesWithId.agent
                      ? "You don't have any assigned forms yet, or there was an issue loading your data."
                      : "No forms are available for renewal management."}
                  </p>
                  <button
                    onClick={fetchRenewalForms}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-lg font-medium mb-2">
                    No forms match the current filters
                  </p>
                  <p className="text-sm text-gray-400">
                    Try adjusting your search criteria or filters.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <table className="w-full bg-white border border-gray-200 rounded-lg table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    <IoPerson className="inline mr-1" />
                    Client Name
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Plan Type
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Duration
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    <IoCalendar className="inline mr-1" />
                    Created Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    <IoCalendar className="inline mr-1" />
                    Renewal Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Status
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Assigned To
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedForms.map((form) => (
                  <React.Fragment key={form._id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleRowExpansion(form._id)}
                    >
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <button
                          className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(form._id);
                          }}
                        >
                          {expandedRows.has(form._id) ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 15l7-7 7 7"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          )}
                        </button>
                      </td>
                      <td
                        className="px-3 py-4 text-sm text-gray-900 truncate"
                        title={
                          form.clientName ||
                          form.name ||
                          form.awareness?.personalDetails?.name ||
                          "N/A"
                        }
                      >
                        {form.clientName ||
                          form.name ||
                          form.awareness?.personalDetails?.name ||
                          "N/A"}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <span>
                          {form.planType ? clientsStore.camelToTitleCase(form.planType) : "N/A"}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        {getRenewalTypeLabel(form.planDuration)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        {formatDate(form.createdAt)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div>
                          <div>{formatDate(form.calculatedRenewalDate)}</div>
                          <div className="text-xs text-gray-500">
                            {getDaysUntilRenewal(form)}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        {getStatusBadge(form)}
                      </td>
                      <td
                        className="px-3 py-4 text-sm text-gray-900 truncate"
                        title={form.assignedTo?.name || "Unassigned"}
                      >
                        {form.assignedTo?.name || "Unassigned"}
                      </td>
                      <td className="px-3 py-4 text-right text-sm font-medium">
                        {!form.renewalSent &&
                          (form.renewalStatus === "overdue" ||
                            form.renewalStatus === "due-soon") && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendRenewal(form);
                              }}
                              className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded border border-blue-600 hover:bg-blue-50 flex items-center gap-1 mx-auto text-xs"
                            >
                              <IoMail className="text-sm" />
                              Send Renewal
                            </button>
                          )}
                        {form.renewalSent && (
                          <span className="text-green-600 px-3 py-1 rounded border border-green-600 bg-green-50 flex items-center gap-1 mx-auto">
                            <IoMail className="text-sm" />
                            Renewal Sent
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* Expanded Row with Client Details */}
                    {expandedRows.has(form._id) && (
                      <tr>
                        <td colSpan="9" className="px-0 py-0">
                          <div className="bg-gray-50 border-t border-gray-200 p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Personal Details Section */}
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                  <IoPerson className="text-blue-600" />
                                  Personal Details
                                </h4>
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Full Name:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.clientName ||
                                          form.name ||
                                          form.awareness?.personalDetails
                                            ?.name ||
                                          "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Email:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.email ||
                                          form.awareness?.personalDetails
                                            ?.email ||
                                          "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Mobile:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.mobileNumber ||
                                          form.mobile ||
                                          form.awareness?.personalDetails
                                            ?.mobileNumber ||
                                          form.awareness?.personalDetails
                                            ?.mobile ||
                                          form.awareness?.personalDetails
                                            ?.phone ||
                                          "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Date of Birth:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.dateOfBirth ||
                                          form.awareness?.personalDetails
                                            ?.dateOfBirth ||
                                          form.awareness?.personalDetails
                                            ?.dob ||
                                          "N/A"}
                                      </p>
                                    </div>
                                  </div>
                                  {form.awareness?.personalDetails?.address && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Address:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.awareness.personalDetails.address}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Financial & Plan Details Section */}
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                  <IoDocumentText className="text-green-600" />
                                  Financial & Plan Details
                                </h4>
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Plan Type:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.planType ? clientsStore.camelToTitleCase(form.planType) : "N/A"}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Duration:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {getRenewalTypeLabel(form.planDuration)}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Created Date:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {formatDate(form.createdAt)}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Renewal Date:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {formatDate(form.calculatedRenewalDate)}
                                      </p>
                                    </div>
                                  </div>
                                  {form.planAmount && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Plan Amount:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        ₹{form.planAmount.toLocaleString()}
                                      </p>
                                    </div>
                                  )}
                                  {form.awareness?.financialDetails && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Financial Goals:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.awareness.financialDetails
                                          .goals || "N/A"}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Additional Information Section */}
                              <div className="bg-white p-4 rounded-lg border border-gray-200 lg:col-span-2">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                  <IoCalendar className="text-purple-600" />
                                  Additional Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">
                                      Assigned To:
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {form.assignedTo?.name || "Unassigned"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">
                                      Renewal Status:
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {getStatusBadge(form)}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-600">
                                      Days Until Renewal:
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {getDaysUntilRenewal(form)}
                                    </p>
                                  </div>
                                  {form.renewalSent && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-600">
                                        Renewal Email Sent:
                                      </span>
                                      <p className="text-sm text-green-600">
                                        Yes
                                      </p>
                                    </div>
                                  )}
                                  {form.notes && (
                                    <div className="md:col-span-3">
                                      <span className="text-sm font-medium text-gray-600">
                                        Notes:
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {form.notes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredForms.length > 0 && pageCount > 1 && (
        <div className="mt-6 flex justify-center items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredForms.length)} of {filteredForms.length} forms
          </div>
          <ReactPaginate
            nextLabel=">"
            previousLabel="<"
            onPageChange={handlePageChange}
            forcePage={currentPage}
            pageRangeDisplayed={2}
            marginPagesDisplayed={1}
            pageCount={pageCount}
            breakLabel="..."
            breakClassName="page-item"
            breakLinkClassName="page-link px-3 py-2"
            containerClassName="flex items-center gap-1"
            pageClassName="page-item"
            pageLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors border border-gray-300"
            previousClassName="page-item"
            previousLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors border border-gray-300"
            nextClassName="page-item"
            nextLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors border border-gray-300"
            activeClassName="active"
            activeLinkClassName="bg-blue-500 text-white hover:bg-blue-600 font-semibold border-blue-500"
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="cursor-not-allowed"
            renderOnZeroPageCount={null}
          />
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedForm && (
        <div className="fixed inset-0 z-50 backdrop-blur-lg bg-black bg-opacity-30 flex items-center justify-center">
          <div className="bg-white shadow-xl rounded-lg w-[95vw] md:w-[60vw] max-w-2xl p-6 relative">
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-2xl"
              onClick={() => setShowEmailModal(false)}
            >
              &times;
            </button>

            <h3 className="text-xl font-semibold mb-4 text-gray-800">
              Send Renewal Email
            </h3>

            <div className="space-y-4">
              {/* Client Info Summary */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">
                  Client Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm text-blue-700">
                  <div>
                    <span className="font-medium">Name:</span>
                    <span className="ml-2">
                      {selectedForm.clientName ||
                        selectedForm.name ||
                        selectedForm.awareness?.personalDetails?.name ||
                        "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Plan:</span>
                    <span className="ml-2">
                      {selectedForm.planType ? clientsStore.camelToTitleCase(selectedForm.planType) : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Duration:</span>
                    <span className="ml-2">
                      {getRenewalTypeLabel(selectedForm.planDuration)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Renewal Date:</span>
                    <span className="ml-2">
                      {formatDate(selectedForm.calculatedRenewalDate)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Email Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Email
                  </label>
                  <input
                    type="email"
                    value={emailData.to}
                    onChange={(e) =>
                      setEmailData({ ...emailData, to: e.target.value })
                    }
                    placeholder="Client email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) =>
                      setEmailData({ ...emailData, subject: e.target.value })
                    }
                    placeholder="Email subject"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={emailData.body}
                    onChange={(e) =>
                      setEmailData({ ...emailData, body: e.target.value })
                    }
                    rows={8}
                    placeholder="Email message"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={sendRenewalEmail}
                  disabled={sendingEmail}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 ${sendingEmail
                    ? "bg-blue-300 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                >
                  <IoMail className="text-sm" />
                  {sendingEmail ? "Sending..." : "Send Email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Loader loader={loading} text="Loading renewal forms..." />
    </div>
  );
}

export default Renewal;
